import { contactError, contactNotes } from '../lib/bookingContact';
import BookingContactFields from '../components/BookingContactFields';
import { serviceCatalog, getSelectedService } from '../lib/catalog';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';

function BookingDetailsPage() {
  const { slotId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingData, setBookingData] = useState({
    service_name: getSelectedService()?.name || '',
    service_price: getSelectedService()?.price || 0,
    notes: '',
    phone: '',
    instagram: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Liste des services disponibles
  const availableServices = serviceCatalog;

  useEffect(() => {
    fetchSlotDetails();
  }, [slotId]);

  const fetchSlotDetails = async () => {
    try {
      // Récupérer tous les créneaux et filtrer pour trouver celui qui correspond au slotId
      const response = await apiService.getSlots(false);
      const foundSlot = response.find(slot => slot.id === slotId);
      
      if (foundSlot) {
        // Vérifier si le créneau est toujours disponible
        if (!foundSlot.is_available) {
          setError('Ce créneau n\'est plus disponible. Veuillez en choisir un autre.');
          setSlot(null);
        } else {
          setSlot(foundSlot);
        }
      } else {
        setError('Créneau non trouvé');
      }
    } catch (err) {
      console.error('Erreur lors du chargement du créneau:', err);
      setError('Impossible de charger les détails du créneau');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non disponible';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date invalide';
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Heure non disponible';
    return timeString;
  };

  const handleServiceSelect = (service) => {
    setBookingData({
      ...bookingData,
      service_name: service.name,
      service_price: service.price
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier que le créneau est bien chargé
    if (!slot) {
      setError('Le créneau n\'est pas disponible. Veuillez actualiser la page.');
      return;
    }

    // Vérifier que le créneau est toujours disponible
    if (!slot.is_available) {
      setError('Ce créneau n\'est plus disponible. Veuillez retourner au tableau de bord et choisir un autre créneau.');
      return;
    }
    
    if (!bookingData.service_name) {
      setError('Veuillez sélectionner un service');
      return;
    }

    if (!bookingData.service_price || bookingData.service_price === 0) {
      setError('Veuillez sélectionner un service valide');
      return;
    }

    const error = contactError(bookingData);
    if (error) { setError(error); return; }
    if (submitting) return;
    setSubmitting(true);
    setError('');

    try {
      const notesWithMetadata = contactNotes(bookingData);

      // Ensure service_price is a number and slot_id is a string
      const appointmentData = {
        slot_id: String(slotId),
        service_name: String(bookingData.service_name),
        service_price: parseFloat(bookingData.service_price),
        notes: notesWithMetadata
      };

      // console.log('Sending appointment data:', appointmentData);
      // console.log('Slot details:', { id: slot.id, is_available: slot.is_available, date: slot.date, start_time: slot.start_time });

      await apiService.createAppointment(appointmentData);

      navigate('/mon-espace', { 
        state: { message: 'Rendez-vous réservé avec succès!' } 
      });
    } catch (err) {
      console.error('Erreur lors de la réservation:', err);
      console.error('Response data:', err.response?.data);
      
      // Message d'erreur plus détaillé
      let errorMessage = 'Erreur lors de la réservation. Veuillez réessayer.';
      if (err.response?.status === 422) {
        errorMessage = 'Données invalides. Vérifiez que tous les champs sont remplis correctement.';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.detail || 'Ce créneau n\'est plus disponible.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error && !slot) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 flex items-center justify-center p-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-light text-stone-800 mb-4">Erreur</h2>
          <p className="text-stone-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/client-dashboard')}
            className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-2 rounded-full hover:from-amber-600 hover:to-amber-700 transition-all"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/20 to-stone-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* En-tête */}
        <div className="bg-white/60 backdrop-blur-md rounded-3xl shadow-xl p-8 mb-6">
          <h1 className="text-3xl font-light text-stone-800 mb-2">Réserver votre rendez-vous</h1>
          <p className="text-stone-500 text-sm">Complétez les informations ci-dessous</p>
        </div>

        {/* Informations du créneau */}
        <div className="bg-white/60 backdrop-blur-md rounded-3xl shadow-xl p-8 mb-6">
          <h2 className="text-xl font-light text-stone-700 mb-4 flex items-center">
            <span className="mr-2">📅</span>
            Créneau sélectionné
          </h2>
          <div className="space-y-2 text-stone-600">
            <p className="flex items-center">
              <span className="font-medium mr-2">Date:</span>
              {formatDate(slot?.date)}
            </p>
            <p className="flex items-center">
              <span className="font-medium mr-2">Heure:</span>
              {formatTime(slot?.start_time)} - {formatTime(slot?.end_time)}
            </p>
          </div>
        </div>

        {/* Formulaire de réservation */}
        <form onSubmit={handleSubmit} className="bg-white/60 backdrop-blur-md rounded-3xl shadow-xl p-8">
          {/* Sélection du service */}
          <div className="mb-8">
            <h2 className="text-xl font-light text-stone-700 mb-4 flex items-center">
              <span className="mr-2">✨</span>
              Choisissez votre service
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableServices.map((service, index) => (
                <div
                  key={index}
                  onClick={() => handleServiceSelect(service)}
                  className={`p-4 rounded-2xl border-2 cursor-pointer transition-all hover:scale-[1.02] ${
                    bookingData.service_name === service.name
                      ? 'border-amber-400 bg-amber-50/50 shadow-md'
                      : 'border-stone-200 bg-white/40 hover:border-amber-300'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-stone-800">{service.name}</h3>
                    <span className="text-amber-600 font-medium text-sm">{service.priceDisplay}</span>
                  </div>
                  <p className="text-xs text-stone-500 mb-1">{service.description}</p>
                  <p className="text-xs text-stone-400">{service.duration}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Détails de la réservation */}
          <div className="space-y-6 mb-8">
            <h2 className="text-xl font-light text-stone-700 mb-4 flex items-center">
              <span className="mr-2">📝</span>
              Détails de votre réservation
            </h2>

            <BookingContactFields value={bookingData} onChange={setBookingData} />

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Notes supplémentaires (optionnel)
              </label>
              <textarea
                value={bookingData.notes}
                onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                rows="3"
                placeholder="Informations complémentaires..."
                className="w-full px-4 py-3 rounded-xl border border-stone-200 bg-white/60 focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all resize-none"
              />
            </div>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Boutons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/client-dashboard')}
              className="flex-1 px-6 py-3 rounded-full border-2 border-stone-300 text-stone-700 hover:bg-stone-50 transition-all"
              disabled={submitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !bookingData.service_name}
              className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
            >
              {submitting ? 'Réservation en cours...' : 'Confirmer la réservation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BookingDetailsPage;
